import { Css, emptyCell, GridColumn, GridTable, RowStyles, simpleHeader } from "@homebound/beam";
import { Price } from "src/components";
import { Number } from "src/components/Number";
import { CommitmentOrChangeOrderPdf_ChangeOrderFragment } from "src/routes/pdfs/CommitmentOrChangeOrderPdf/CommitmentOrChangeOrderPdf.generated";
import { sanitizeHtml } from "src/utils";
import { createPdfLineItemRows, HeaderRow, LineItemRow, OptionGroupRow, SpecificationsRow } from "./CommitmentTable";

type CommitmentChangeOrderTableProps = { commitmentChangeOrder: CommitmentOrChangeOrderPdf_ChangeOrderFragment };

export function CommitmentChangeOrderTable({ commitmentChangeOrder }: CommitmentChangeOrderTableProps) {
  return (
    <>
      <div css={Css.df.fdr.ttu.mt2.$}>
        <div css={Css.df.f1.p1.$}>Line Items</div>
        <div css={Css.df.f1.jcc.p1.fwb.$}>Previous</div>
        <div css={Css.df.f1.jcc.p1.fwb.$}>New</div>
      </div>
      <GridTable
        as="table"
        columns={commitmentChangeOrderColumns}
        rows={createRows(commitmentChangeOrder)}
        rowStyles={rowStyles}
      />
    </>
  );
}

type SalesTaxRow = { kind: "salesTax"; data: { previousSalesTax?: number; newSalesTax?: number } };
type TotalRow = { kind: "total"; data: { previousTotal?: number; newTotal?: number } };
type CommitmentChangeOrderRow = LineItemRow | OptionGroupRow | HeaderRow | SalesTaxRow | TotalRow | SpecificationsRow;

const commitmentChangeOrderColumns: GridColumn<CommitmentChangeOrderRow>[] = [
  {
    header: "Item Code",
    group: (groupName) => ({
      content: groupName,
      colspan: 11, // Can span across entire table
      css: Css.sm.px1.$,
    }),
    data: (lineItem) => lineItem.projectItem.item.fullCode,
    specifications: emptyCell,
    salesTax: emptyCell,
    total: emptyCell,
  },
  {
    header: "Description",
    group: emptyCell,
    data: (lineItem) => lineItem.projectItem.description,
    specifications: (specification) => ({
      content: (
        <p data-testid="specification">
          Note: <span dangerouslySetInnerHTML={{ __html: sanitizeHtml(specification || "") }} />
        </p>
      ),
      colspan: 10, // Can span across entire table
      css: Css.sm.$,
    }),
    salesTax: emptyCell,
    total: emptyCell,
  },
  {
    header: "CostType",
    group: emptyCell,
    data: (lineItem) => lineItem.projectItem.costTypeDetail.name,
    specifications: emptyCell,
    salesTax: emptyCell,
    total: emptyCell,
  },
  // previous line item data
  {
    header: "Qty",
    group: emptyCell,
    data: (lineItem) => lineItem.originalQuantity,
    specifications: emptyCell,
    salesTax: emptyCell,
    total: emptyCell,
  },
  {
    header: "Unit",
    group: emptyCell,
    data: (lineItem) => lineItem.projectItem.unitOfMeasure.name,
    specifications: emptyCell,
    salesTax: emptyCell,
    total: "Previous Total:",
  },
  {
    header: "Unit Cost",
    group: emptyCell,
    data: (lineItem) => <Price valueInCents={lineItem.originalTaxableCostInCents / (lineItem.originalQuantity || 1)} />,
    specifications: emptyCell,
    salesTax: emptyCell,
    total: emptyCell,
  },
  {
    header: "Subtotal",
    group: emptyCell,
    data: (lineItem) => <Price valueInCents={lineItem.originalTaxableCostInCents} />,
    specifications: emptyCell,
    salesTax: ({ previousSalesTax }) => <Price valueInCents={previousSalesTax} />,
    total: ({ previousTotal }) => <Price valueInCents={previousTotal} />,
  },
  // new line item data
  {
    header: "Qty",
    group: emptyCell,
    data: (lineItem) => (
      <div css={Css.df.fdc.$}>
        <Number value={lineItem.newQuantity} />
        <Number displayDirection value={lineItem.newQuantity - lineItem.originalQuantity} />
      </div>
    ),
    specifications: emptyCell,
    salesTax: emptyCell,
    total: emptyCell,
  },
  {
    header: "Unit",
    group: emptyCell,
    data: (lineItem) => lineItem.projectItem.unitOfMeasure.name,
    specifications: emptyCell,
    salesTax: emptyCell,
    total: emptyCell,
  },
  {
    header: "Unit Cost",
    group: emptyCell,
    data: (lineItem) => {
      const newUnitCost = lineItem.newTaxableCostInCents / (lineItem.newQuantity || 1);
      const originalUnitCost = lineItem.originalTaxableCostInCents / (lineItem.originalQuantity || 1);
      return (
        <div css={Css.df.fdc.$}>
          <Price valueInCents={newUnitCost} />
          {newUnitCost !== originalUnitCost && <Price displayDirection valueInCents={newUnitCost - originalUnitCost} />}
        </div>
      );
    },
    specifications: emptyCell,
    salesTax: "New Sales Tax:",
    total: "New Total:",
  },
  {
    header: "Subtotal",
    group: emptyCell,
    data: (lineItem) => {
      return (
        <div css={Css.df.fdc.$}>
          <Price valueInCents={lineItem.newTaxableCostInCents} />
          <Price displayDirection valueInCents={lineItem.taxableCostInCents} />
        </div>
      );
    },
    specifications: emptyCell,
    salesTax: ({ newSalesTax }) => <Price valueInCents={newSalesTax} />,
    total: ({ newTotal }) => <Price valueInCents={newTotal} />,
  },
];

function createRows(commitmentChangeOrder: CommitmentOrChangeOrderPdf_ChangeOrderFragment) {
  const specOptionId = commitmentChangeOrder.commitment.project.readyPlanConfig?.specOption?.readyPlanOption.id;
  const lineItems = commitmentChangeOrder.lineItems;
  const rows = createPdfLineItemRows(lineItems, specOptionId);
  const previousSalesTax = lineItems?.sum((cli) => cli.originalTaxInCents);
  const newSalesTax = lineItems?.sum((cli) => cli.newTaxInCents);
  const previousTotal = lineItems?.sum((cli) => cli.originalTotalCostInCents);
  const newTotal = lineItems?.sum((cli) => cli.newTotalCostInCents);
  return [
    simpleHeader,
    ...rows,
    ...(previousSalesTax !== 0 || newSalesTax !== 0
      ? [{ kind: "salesTax" as const, id: "salesTax", data: { previousSalesTax, newSalesTax } }]
      : []),
    {
      kind: "total" as const,
      id: "total",
      data: { previousTotal, newTotal },
    },
  ];
}

// adding border-left to Quantity columns
const qtyHeaderStyles = Css.addIn("& > th:nth-of-type(4), & > th:nth-of-type(8)", Css.bl.bcGray400.pl2.$).$;
const qtyCellBorderStyles = Css.addIn("& > td:nth-of-type(4), & > td:nth-of-type(8)", Css.bl.bcGray400.pl2.$).$;

const rowStyles: RowStyles<CommitmentChangeOrderRow> = {
  data: { cellCss: Css.p1.vat.$, rowCss: qtyCellBorderStyles },
  header: { rowCss: qtyHeaderStyles },
};
